import { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const CUSTOM_ORDER_STATUS_COLOR = "#0071b9";
const CUSTOM_TAG_STORAGE_KEY = "gf-order-status-custom-tags";
const CUSTOM_TAG_MAX_LENGTH = 40;

const normalizeTagLabel = (value) => String(value || "").trim().replace(/\s+/g, " ");
const tagKey = (value) => normalizeTagLabel(value).toLowerCase();

const VIEWPORT_GUTTER = 8;
const MENU_GAP = 4;
/** Menus stay short in either direction. Extra tags scroll inside the list. */
const MENU_MAX_HEIGHT = 280;

/** Document coordinates for the tag menu, including the trigger's top edge so the menu can open upward. */
export function orderStatusMenuPosition(rect) {
  return {
    top: rect.bottom + window.scrollY,
    anchorTop: rect.top + window.scrollY,
    left: rect.left + window.scrollX,
    width: rect.width,
  };
}

/** Keep the menu inside the window. Open it above the trigger when the rows below cannot show the list. */
export function placeOrderStatusMenu(position, contentHeight, allowCustomTag) {
  const anchorBottom = (position?.top || 0) - window.scrollY;
  const anchorTop = (position?.anchorTop != null ? position.anchorTop : (position?.top || 0) - 40) - window.scrollY;
  const width = Math.max(position?.width || 0, allowCustomTag ? 240 : 200);
  let left = (position?.left || 0) - window.scrollX;
  const maxLeft = window.innerWidth - width - VIEWPORT_GUTTER;
  if (left > maxLeft) left = Math.max(VIEWPORT_GUTTER, maxLeft);
  if (left < VIEWPORT_GUTTER) left = VIEWPORT_GUTTER;

  const spaceBelow = Math.max(0, window.innerHeight - anchorBottom - VIEWPORT_GUTTER - MENU_GAP);
  const spaceAbove = Math.max(0, anchorTop - VIEWPORT_GUTTER - MENU_GAP);
  const fullHeight = Math.max(contentHeight || 0, 40);
  const preferred = Math.min(fullHeight, MENU_MAX_HEIGHT);
  const openUp = spaceBelow < preferred && spaceAbove > spaceBelow;
  const available = Math.max(120, openUp ? spaceAbove : spaceBelow);
  const maxHeight = Math.min(preferred, available);
  let top = openUp ? anchorTop - maxHeight - MENU_GAP : anchorBottom + MENU_GAP;
  const bottomLimit = window.innerHeight - VIEWPORT_GUTTER;
  if (top + maxHeight > bottomLimit) {
    top = Math.max(VIEWPORT_GUTTER, bottomLimit - maxHeight);
  }
  if (top < VIEWPORT_GUTTER) top = VIEWPORT_GUTTER;

  return { top, left, width, maxHeight };
}

export function loadStoredCustomOrderStatusTags() {
  try {
    const raw = JSON.parse(localStorage.getItem(CUSTOM_TAG_STORAGE_KEY) || "[]");
    if (!Array.isArray(raw)) return [];
    const seen = new Set();
    return raw.reduce((tags, tag) => {
      const label = normalizeTagLabel(tag?.label).slice(0, CUSTOM_TAG_MAX_LENGTH);
      const key = tagKey(label);
      if (!label || seen.has(key)) return tags;
      seen.add(key);
      tags.push({ label, color: CUSTOM_ORDER_STATUS_COLOR, custom: true });
      return tags;
    }, []);
  } catch {
    return [];
  }
}

function saveStoredCustomOrderStatusTags(tags) {
  try {
    localStorage.setItem(CUSTOM_TAG_STORAGE_KEY, JSON.stringify(tags));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Custom tags already saved on loaded orders, so they can be picked again. */
export function collectCustomOrderStatusTags(statusMaps, builtInTags = []) {
  const builtIn = new Set((builtInTags || []).map((tag) => tagKey(tag?.label)));
  const seen = new Set();
  const found = [];
  const add = (tag) => {
    const label = normalizeTagLabel(typeof tag === "string" ? tag : tag?.label).slice(0, CUSTOM_TAG_MAX_LENGTH);
    const key = tagKey(label);
    if (!label || builtIn.has(key) || seen.has(key)) return;
    const color = String(tag?.color || "").trim().toLowerCase();
    const isCustom = tag?.custom === true || color === CUSTOM_ORDER_STATUS_COLOR.toLowerCase();
    if (!isCustom) return;
    seen.add(key);
    found.push({ label, color: CUSTOM_ORDER_STATUS_COLOR, custom: true });
  };
  (statusMaps || []).forEach((map) => {
    Object.values(map || {}).forEach((tags) => {
      if (Array.isArray(tags)) tags.forEach(add);
    });
  });
  return found;
}

const OrderStatusDropdown = ({
  visible,
  position,
  orderStatuses = [],
  selectedTags = [],
  onToggleTag,
  allowCustomTag = false,
  savedCustomTags = [],
  menuClassName = "",
}) => {
  const [draft, setDraft] = useState("");
  const [storedCustomTags, setStoredCustomTags] = useState(loadStoredCustomOrderStatusTags);
  const listRef = useRef(null);
  const formRef = useRef(null);
  const [placement, setPlacement] = useState(null);

  const builtInKeys = new Set((orderStatuses || []).map((tag) => tagKey(tag?.label)));
  const customTags = [];
  const seenCustom = new Set();
  [...storedCustomTags, ...savedCustomTags].forEach((tag) => {
    const label = normalizeTagLabel(tag?.label).slice(0, CUSTOM_TAG_MAX_LENGTH);
    const key = tagKey(label);
    if (!label || builtInKeys.has(key) || seenCustom.has(key)) return;
    seenCustom.add(key);
    customTags.push({ label, color: CUSTOM_ORDER_STATUS_COLOR, custom: true });
  });
  const tags = [...(orderStatuses || []), ...customTags];
  const tagSignature = tags.map((tag) => tag.label).join("\n");

  useLayoutEffect(() => {
    if (!visible) {
      setPlacement(null);
      return undefined;
    }
    const update = () => {
      const listHeight = listRef.current?.scrollHeight || 0;
      if (!listRef.current || listHeight <= 0) return;
      const formHeight = formRef.current?.offsetHeight || 0;
      const contentHeight = listHeight + formHeight + 2;
      const next = placeOrderStatusMenu(position, contentHeight, allowCustomTag);
      setPlacement((prev) => {
        if (
          prev &&
          prev.top === next.top &&
          prev.left === next.left &&
          prev.width === next.width &&
          prev.maxHeight === next.maxHeight
        ) {
          return prev;
        }
        return next;
      });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [visible, position, allowCustomTag, tagSignature]);

  if (!visible) return null;

  const box = placement || placeOrderStatusMenu(position, 640, allowCustomTag);

  const addCustomTag = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const label = normalizeTagLabel(draft).slice(0, CUSTOM_TAG_MAX_LENGTH);
    const key = tagKey(label);
    if (!label) return;

    const builtIn = orderStatuses.find((tag) => tagKey(tag?.label) === key);
    const existingCustom = customTags.find((tag) => tagKey(tag.label) === key);
    const tag = builtIn || existingCustom || { label, color: CUSTOM_ORDER_STATUS_COLOR, custom: true };

    if (!builtIn && !existingCustom) {
      const next = [...storedCustomTags.filter((item) => tagKey(item.label) !== key), tag];
      setStoredCustomTags(next);
      saveStoredCustomOrderStatusTags(next);
    }

    const alreadySelected = selectedTags.some((item) => tagKey(item?.label) === key);
    if (!alreadySelected) onToggleTag(tag);
    setDraft("");
  };

  return createPortal(
    <div
      className={`order-status-dropdown${menuClassName ? ` ${menuClassName}` : ""}`}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: "fixed",
        top: box.top,
        left: box.left,
        zIndex: 9999,
        backgroundColor: "white",
        border: "1px solid #ced4da",
        borderRadius: "4px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        width: box.width,
        display: "flex",
        flexDirection: "column",
        maxHeight: box.maxHeight,
        overflow: "hidden",
      }}
    >
      <div ref={listRef} style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
        {tags.map((tag) => {
          const isSelected = selectedTags.some((selection) => selection.label === tag.label);
          return (
            <div
              key={`${tag.custom ? "custom" : "built-in"}-${tag.label}`}
              className="p-2 d-flex align-items-center"
              style={{
                cursor: "pointer",
                backgroundColor: isSelected ? "#f8f9fa" : "white",
                borderBottom: "1px solid #f1f1f1",
              }}
              onClick={() => onToggleTag(tag)}
            >
              {tag.icon ? (
                <i className={`${tag.icon} me-2`} aria-hidden="true" style={{ color: tag.color, fontSize: "14px" }}></i>
              ) : (
                <div
                  style={{
                    width: "12px",
                    height: "12px",
                    borderRadius: "2px",
                    backgroundColor: tag.color,
                    marginRight: "8px",
                  }}
                ></div>
              )}
              <span style={{ fontSize: "0.875rem" }}>{tag.label}</span>
              {isSelected && <span className="ms-auto">✓</span>}
            </div>
          );
        })}
      </div>
      {allowCustomTag && (
        <form
          ref={formRef}
          onSubmit={addCustomTag}
          style={{
            display: "flex",
            flexShrink: 0,
            gap: "6px",
            padding: "8px",
            borderTop: "1px solid #dee2e6",
            backgroundColor: "#fff",
          }}
        >
          <input
            type="text"
            value={draft}
            maxLength={CUSTOM_TAG_MAX_LENGTH}
            placeholder="Custom tag"
            aria-label="Custom tag name"
            onChange={(event) => setDraft(event.target.value)}
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: "0.8rem",
              border: "1px solid #ced4da",
              borderRadius: "4px",
              padding: "4px 6px",
            }}
          />
          <button
            type="submit"
            disabled={!normalizeTagLabel(draft)}
            style={{
              backgroundColor: CUSTOM_ORDER_STATUS_COLOR,
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              padding: "4px 10px",
              fontSize: "0.75rem",
              cursor: normalizeTagLabel(draft) ? "pointer" : "not-allowed",
              opacity: normalizeTagLabel(draft) ? 1 : 0.6,
            }}
          >
            Add
          </button>
        </form>
      )}
    </div>,
    document.body
  );
};

export default OrderStatusDropdown;
